import { app } from 'app/app';
import { user } from 'app/user';
import { AppController } from 'app/controller';
import {
    API_ENDPOINT_JOBALARM_MODAL_DATA,
    ENDPOINT_JOBS,
    ENDPOINT_USER_LOGIN,
} from 'app/endpoints';
import { userWatchlist } from 'app/userWatchlist';
import { filterObject } from 'util/filter';
import { getJson } from 'util/xhr';
import { mediaQueries } from 'util/mediaQueries';
import { getParams, params as urlWithParams, stringToSlug } from 'util/url';
import { sendPageView, setPageType, trackGa4 } from 'util/googleAnalytics';
import deepEqual from 'util/deepEqual';
import { hasConsentGrantedOrDenied, onConsentChange } from 'util/consent';
import { readScreenReaderText } from 'util/a11y';
import { MasterLayout } from 'layout/master/main';
import { JobsSearchform } from 'module/jobsSearchform/main';
import { AlarmDisruptorPill } from 'module/alarmDisruptorPill/main';
import { JobsSearchList } from 'module/jobsSearchList/main';
import { JobDetail } from 'module/jobDetail/main';
import { JobsEmptyState } from 'module/jobsEmptyState/main';
import { LoadingOverlay } from 'module/loadingOverlay/main';
import { SubNavigation } from 'module/subNavigation/main';
import { SmallFooter } from 'module/smallFooter/main';
import { JobSearchHistory } from 'util/client-side-storage/jobSearchHistory';
import { FormModal } from 'module/formModal/main';
import Stickyfill from 'stickyfilljs';
import { showSnackbar } from 'util/snackbar';
import './styles.scss';

let selectedJobId = false;
let noCache = false;
let page = false;
let timeout = 0;
const defaultSalaryId = 10006;

class JobsSearchController extends AppController {
    ready() {
        app.events.on('firstUserInteraction', () => this.onFirstUserInteraction());
        let jobDetailContentActive = false;
        noCache = !!window.location.search.split('noCache=')[1];
        page = getParams(window.location.search).page;

        if (window.history.state && window.history.state.jobDetailContent) {
            jobDetailContentActive = window.history.state.jobDetailContent;
        }

        this.props.jobsSearchform = Object.assign(
            this.jobsSearchform.getSerializedProps(),
        );

        if (window.location.hash) {
            selectedJobId = window.location.hash.substring(1);
        }

        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        this.initHistoryApi();

        // remove page param from old saved searches
        if (page) {
            this.replaceState(
                this.props.jobsSearchform,
                document.title,
                this.generateCleanUrl(Object.assign(this.props.jobsSearchform, { selectedJobId })),
            );
        }

        this.addToJobSearchCache();
        this.sendShowEvents();
        this.sendListTracking();

        if (selectedJobId) {
            this.openJobDetail(selectedJobId, true, true, jobDetailContentActive);
            if (this.jobsSearchList.insideListing(selectedJobId)) {
                const url = `${ENDPOINT_JOBS}/${selectedJobId}`;
                setTimeout(() => {
                    this.sendAdWordsTracking(selectedJobId);
                    setPageType(this.props.layout?.jobBundleLabel);
                    sendPageView(document.title, url);
                    if (this.jobDetail) this.jobDetail.sendShowEvents();
                }, 2000);
            }
        }

        if (this.props.jobsSearchform.keywords && !mediaQueries.isMobile()) {
            this.hiringEasterEgg();
        }

        onConsentChange(() => this.addToJobSearchCache());

        // a11y - Focus search results when coming from home page
        const { focusResults } = getParams(window.location.search);
        if (focusResults) {
            document.getElementById('jobsListHeaderScreenReaderHelper').focus();
        }
    }

    async addToJobSearchCache() {
        const { keywords, locations } = this.jobsSearchform.getPropsFromDom();
        if (keywords.length > 0 || locations.length > 0) {
            await JobSearchHistory.add({ keyword: keywords, location: locations });
            this.jobsSearchform.updateHistoryItems();
        }
    }

    hiringEasterEgg() {
        // eslint-disable-next-line no-console
        console?.log('%c  _                _                    _   \n'
            + ' | |____ _ _ _ _ _(_)___ _ _ ___   __ _| |_ \n'
            + ' | / / _` | \'_| \'_| / -_) \'_/ -_)_/ _` |  _|\n'
            + ' |_\\_\\__,_|_| |_| |_\\___|_| \\___(_)__,_|\\__|', 'color: #22a15d; font-size: 12;');

        // eslint-disable-next-line no-console
        console?.log('%cWe’re hiring! Join us!', 'font-size: 18px;');
        // eslint-disable-next-line no-console
        console?.log('%chttps://www.karriere.at/b/karriere-at', 'font-size: 14px;');
    }

    onPopState(state) {
        if (!state) return; // hashbang fix
        const shareModalActive = state.shareModalOpen ? state.shareModalOpen : false;
        delete state.shareModalOpen;
        const oldSearchformProps = this.props.jobsSearchform;

        const editFormModalActive = state.editFormModal
            ? state.editFormModal : false;

        this.setProps(state);
        this.jobsSearchform.setProps({
            ...this.props.jobsSearchform,
            collapsedSearchform: this.jobsSearchform.getPropsFromDom().collapsedSearchform,
        }, true);

        const jobDetailContentActive = state.jobDetailContent ? state.jobDetailContent.active : false;
        const detailModalIsOpen = this.jobDetail?.props.modalOpen === true;
        let sendSearchRequest = true;

        if (window.location.hash) {
            selectedJobId = window.location.hash.substring(1);
        } else {
            selectedJobId = false;
            if (!mediaQueries.isMobile()) {
                this.jobsSearchList.removeHighlightedJobItems();
                window.scrollTo(0, 0);
            }
        }

        if (mediaQueries.isMobile()) {
            if (jobDetailContentActive) {
                if (!this.jobDetail.props.modalOpen) {
                    this.jobDetail.openModal();
                } else if (shareModalActive) {
                    this.jobDetail.openShareModal();
                    return;
                } else if (this.jobDetail.isShareModalOpen()) {
                    this.jobDetail.closeShareModal();
                    return;
                }
                if (mediaQueries.isMobile()) {
                    sendSearchRequest = false;
                }
            } else if (!jobDetailContentActive || detailModalIsOpen) {
                this.jobDetail.closeModal();
            }

            if (editFormModalActive || (this.editModal?.isOpen())) {
                if (editFormModalActive && this.alarmDisruptor.props.exists) {
                    this.editModal.openModal();
                } else {
                    this.editModal.closeModal();
                }
            }
        }

        const tempSearchformState = { ...state.jobsSearchform };
        const filteredSearchformProps = this.filterRequest(filterObject(oldSearchformProps, ['filter', 'proximity']));
        const filteredStateSearchformProps = this.filterRequest(
            filterObject(tempSearchformState, ['filter', 'proximity']),
        );
        const isEqualJobsSearchRequest = deepEqual(filteredSearchformProps, filteredStateSearchformProps);

        if (state.jobsSearchform && !state.jobsSearchList && sendSearchRequest) {
            // if we haz a searchrequest and no joblist we do jobsearch weeeeee
            this.sendSearchRequest(state.jobsSearchform, true, jobDetailContentActive, !isEqualJobsSearchRequest);
        } else if (!isEqualJobsSearchRequest) {
            sendPageView();
            this.addToJobSearchCache();
            this.sendShowEvents();
            this.sendListTracking();
        }

        if (jobDetailContentActive) {
            const jobId = state.jobDetailContent.jobDetail.id;
            const { title } = state.layout.meta;

            const url = `${ENDPOINT_JOBS}/${jobId}`;
            this.sendAdWordsTracking(jobId);
            setPageType(state.layout.jobBundleLabel);
            sendPageView(title, url);
            if (this.jobDetail) this.jobDetail.sendShowEvents();
        }
    }

    domBindings() {
        return {
            bgContainer: '.c-jobsSearch__bgContainer',
            subNavigation: '.c-jobsSearch .m-subNavigation',
            editModal: '.c-jobsSearch__editModal .m-formModal',
            jobsSearchform: '.c-jobsSearch__searchform .m-jobsSearchform',
            jobsSearchList: '.c-jobsSearch__listing .m-jobsSearchList',
            jobDetailContainer: '.c-jobsSearch__detail',
            jobDetail: '.c-jobsSearch__detail .m-jobDetail',
            detailInner: '.c-jobsSearch__detailInner',
            loadingOverlayDetail: '.c-jobsSearch__detailInner .m-loadingOverlay',
            loadingOverlayListing: '.c-jobsSearch__jobsSearchList .m-loadingOverlay',
            jobsEmptyState: '.c-jobsSearch .m-jobsEmptyState',
            alarmDisruptorPillContainer: '.c-jobsSearch__alarmDisruptorPill',
            alarmDisruptorPill: '.c-jobsSearch__alarmDisruptorPill .m-alarmDisruptorPill',
            splitViewContainer: '.c-jobsSearch__splitView',
            loginModal: '.c-jobsSearch__loginModal .m-loginModal',
            smallFooter: '.c-jobsSearch__footer .m-smallFooter',
        };
    }

    subs() {
        this.layout = new MasterLayout(document.documentElement);
        this.jobsSearchform = new JobsSearchform(this.dom.jobsSearchform);
        this.jobsSearchList = new JobsSearchList(this.dom.jobsSearchList);
        this.jobDetail = new JobDetail(this.dom.jobDetail);
        if (this.dom.editModal) {
            this.editModal = new FormModal(this.dom.editModal);
        }
        Stickyfill.add(this.dom.detailInner);
        if (this.dom.jobsEmptyState) {
            this.jobsEmptyState = new JobsEmptyState(this.dom.jobsEmptyState);
        }
        this.loadingOverlayDetail = new LoadingOverlay(this.dom.loadingOverlayDetail);
        this.loadingOverlayListing = new LoadingOverlay(this.dom.loadingOverlayListing);

        if (this.dom.alarmDisruptorPill) {
            this.alarmDisruptor = new AlarmDisruptorPill(this.dom.alarmDisruptorPill);
        }
        if (this.dom.subNavigation) {
            this.subNavigation = new SubNavigation(this.dom.subNavigation);
        }
        if (this.dom.smallFooter) this.smallFooter = new SmallFooter(this.dom.smallFooter);
    }

    subsProps() {
        if (this.props.jobsSearchform) {
            this.jobsSearchform.setProps(this.props.jobsSearchform, true);
        }
        if (this.props.jobDetailContent) {
            this.jobDetail.setProps(this.props.jobDetailContent, true);
        }
        if (this.props.jobsSearchList) {
            this.jobsSearchList.updateProps(this.props.jobsSearchList, true);
            if (this.jobsSearchList.hokifyJobsAd) {
                this.jobsSearchList.hokifyJobsAd.load();
            }
        }
        if (this.props.jobsEmptyState) {
            this.jobsEmptyState.setProps(this.props.jobsEmptyState, true);
        }
        if (this.props.alarmDisruptor) {
            this.alarmDisruptor.setProps(this.props.alarmDisruptor, true);
        }
    }

    subEvents() {
        if (this.jobDetail) {
            if (hasConsentGrantedOrDenied()) {
                this.jobDetail.allowPointerEvents();
            }
            onConsentChange(() => {
                if (hasConsentGrantedOrDenied()) {
                    this.jobDetail.allowPointerEvents();
                }
            });
        }

        this.layout.events.on('getUserJobSearchRequest', (e) => {
            const tempSearchRequest = this.jobsSearchform.getUserJobSearchRequest();
            if (parseInt(tempSearchRequest['salary[]'][0], 10) === defaultSalaryId) {
                delete tempSearchRequest['salary[]'];
            }

            const redirectUrl = encodeURIComponent(this.generateCleanUrl(tempSearchRequest));
            this.layout.openLoginModal(
                `/user/${e.arguments[0] ? 'register' : 'login'}/oauth?redirectUrl=${redirectUrl}`,
                true,
                true,
            );
        });

        this.jobsSearchform.events.on('jobsSearchformSubmit', (searchRequest) => {
            this.newSearchRequest(searchRequest.arguments[0]);
        });

        this.jobsSearchform.events.on('openLoginModal', () => {
            const tempSearchRequest = this.jobsSearchform.getUserJobSearchRequest();
            if (parseInt(tempSearchRequest['salary[]'][0], 10) === defaultSalaryId) {
                delete tempSearchRequest['salary[]'];
            }

            const redirectUrl = encodeURIComponent(this.generateCleanUrl(tempSearchRequest));
            this.layout.openLoginModal(`${ENDPOINT_USER_LOGIN}?redirectUrl=${redirectUrl}`, true, true);
        });

        this.jobsSearchform.events.on('selectedRadiusIndex', (event) => {
            [this.props.selectedRadiusIndex] = event.arguments;
        });

        this.jobsSearchList.events.on('jobClick', (e) => {
            const jobId = e.arguments[0];
            if (jobId !== selectedJobId) {
                this.openJobDetail(jobId);
            }
        });

        this.jobsSearchList.events.on('proposalLinkClicked', (searchRequest) => {
            this.newSearchRequest(searchRequest.arguments[0], false);
        });

        this.jobsSearchList.events.on('changedFilter', (searchRequest) => {
            this.newSearchRequest(searchRequest.arguments[0].searchRequest, false);
        });

        this.jobsSearchList.events.on('radiusRemoved', () => {
            this.props.selectedRadiusIndex = 0;
        });

        this.jobsSearchList.events.on('load', (searchRequest) => {
            // always send empty slugs from jobs loadMoreJobsButton
            searchRequest.arguments[0].keywordSlug = false;
            searchRequest.arguments[0].locationSlug = false;
            this.loadMoreJobsRequest(searchRequest.arguments[0]);
        });

        this.jobsSearchList.events.on('openLoginModal', (e) => {
            this.layout.openLoginModal(e.arguments[0], e.arguments[1]);
        });

        this.jobsSearchList.events.on('addModalStyle', () => {
            this.layout.addModalStyle();
            this.addModalStyles();
        });

        this.jobsSearchList.events.on('removeModalStyle', () => {
            this.layout.removeModalStyle();
            this.removeModalStyles();
        });

        this.jobsSearchList.events.on('jobAlarmResponseSuccess', (response) => {
            this.alarmDisruptor.onResponse(response.arguments[0], true);
        });

        this.jobDetail.events.on('pushState', () => {
            const props = { ...this.getProps() };
            props.shareModalOpen = true;
            const tempSearchFormProps = { ...this.props.jobsSearchform };
            this.pushState(
                props,
                this.props.layout?.meta?.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                false,
            );
        });

        this.jobDetail.events.on('addModalStyle', () => {
            this.layout.addModalStyle();
        });

        this.jobDetail.events.on('removeModalStyle', () => {
            this.layout.removeModalStyle();
        });

        this.jobDetail.events.on('addShareModalStyles', () => {
            this.layout.addModalStyle();
            this.addModalStyles();
        });

        this.jobDetail.events.on('removeShareModalStyles', () => {
            this.layout.removeModalStyle();
            this.removeModalStyles();
        });

        this.jobDetail.events.on('jobContentLoaded', () => {
            this.hideOverlay('detail');
        });

        this.jobDetail.events.on('jobContentLoading', () => {
            this.showOverlay('detail');
        });

        if (this.editModal && this.alarmDisruptor) {
            this.editModal.events.on('jobalarmDeleted', (e) => {
                const { jobalarmId } = e.arguments[0];
                if (parseInt(this.alarmDisruptor.props.jobAlarmId, 10) !== parseInt(jobalarmId, 10)) return;

                this.alarmDisruptor.updateProps({
                    exists: false,
                }, true);

                showSnackbar({
                    text: 'Jobalarm wurde gelöscht.',
                });
            });
            this.editModal.events.on('addModalStyle', () => {
                this.layout.addModalStyle();
                this.addModalStyles();
            });
            this.editModal.events.on('removeModalStyle', () => {
                this.layout.removeModalStyle();
                this.removeModalStyles();
            });
            this.editModal.events.on('pushStateFormModal', () => {
                const props = { ...this.getProps() };
                props.editFormModal = true;
                this.pushState(
                    props,
                    document.title,
                    window.location.href,
                    false,
                );
            });
            this.editModal.events.on('changedJobalarm', (e) => {
                if (e.arguments[4]) this.changedSearchRequest(e.arguments[2]);
            });
            this.editModal.events.on('showSnackbar', () => {
                showSnackbar({
                    text: 'Jobalarm wurde angepasst.',
                });
            });
        }

        if (this.alarmDisruptor) {
            this.alarmDisruptor.events.on('addModalStyle', () => {
                this.layout.addModalStyle();
                this.addModalStyles();
            });
            this.alarmDisruptor.events.on('removeModalStyle', () => {
                this.layout.removeModalStyle();
                this.removeModalStyles();
            });
            this.alarmDisruptor.events.on('openEditModal', (e) => {
                const jobAlarmId = e.arguments[0];
                this.fetch = getJson(
                    `${API_ENDPOINT_JOBALARM_MODAL_DATA}/${jobAlarmId}`,
                    {},
                    (response) => this.editModal.openModalWithData(response),
                );
            });
            this.alarmDisruptor.events.on('jobAlarmResponseSuccess', (response) => {
                this.jobsSearchList.onJobalarmResponseSuccess(response.arguments[0]);
            });
            this.alarmDisruptor.events.on('jobAlarmRemoveResponse', (response) => {
                this.jobsSearchList.onJobAlarmRemoveResponse(response.arguments[0]);
            });
            this.alarmDisruptor.events.on('showSnackbar', (e) => {
                showSnackbar({
                    text: e.arguments[0],
                    icon: (e.arguments[1] && e.arguments[2]) ? false : true,
                    link: e.arguments[1],
                    linkText: e.arguments[2],
                });
            });
        }

        if (!user.isLoggedIn()) {
            userWatchlist.events.on('onUnauthorized', (watchlistObject) => {
                this.layout.openLoginModal(watchlistObject.arguments[0].watchlistObject.state, true, true);
            });
        }
    }

    openJobDetail(jobId, replaceState = false, onReady = false, jobDetailContentActive = false) {
        this.showOverlay('detail');
        this.dom.splitViewContainer.classList.remove('c-jobsSearch__splitView--seo');
        this.jobsSearchList.removeSeoView();

        const url = noCache
            ? `${ENDPOINT_JOBS}/${jobId}?isSplitView=true&noCache=1`
            : `${ENDPOINT_JOBS}/${jobId}?isSplitView=true`;
        this.request = getJson(
            url,
            {},
            (response) => this.onJobDetailResponse(response, replaceState, onReady, jobDetailContentActive),
            (error) => this.onJobDetailError(error),
        );
    }

    async onJobDetailResponse(response, replaceState, onReady, jobDetailContentActive) {
        const jobId = response.jobDetailContent?.jobDetail?.id;
        const isInactive = response.jobDetailContent?.jobDetail?.isInactive;
        const title = response.layout?.meta?.title;
        this.request = false;

        if (!this.jobsSearchList.insideListing(jobId)) {
            let jobsInfoBox = {};

            if (isInactive) {
                jobsInfoBox = Object.assign(response.jobDetailContent.jobsInfoBox, { wrap: true });
            } else {
                jobsInfoBox = {
                    active: true,
                    closeable: false,
                    wrap: true,
                    text: 'Der ausgeschriebene Job befindet sich nicht mehr in den aktuellen Suchergebnissen.',
                    link: {
                        active: true,
                        url: `/jobs/${jobId}`,
                        label: 'Job anzeigen',
                    },
                };
            }

            this.jobsSearchList.updateInfoBox(jobsInfoBox);

            const tempSearchFormProps = { ...this.props.jobsSearchform };
            const tempProps = { ...this.getProps(), jobsInfoBox };
            this.replaceState(
                tempProps,
                document.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
            );
            this.hideOverlay('detail');
            return;
        }

        if (this.jobsEmptyState) {
            this.jobsEmptyState.updateProps({ active: false }, true);
            const emptyStateActive = this.props.jobsEmptyState ? this.props.jobsEmptyState.active : false;
            if (emptyStateActive) {
                this.props.jobsEmptyState.active = false;
            }
        }

        if (!mediaQueries.isMobile()) {
            if (this.props.jobsSearchList !== undefined) {
                this.props.jobsSearchList.activeItems.items = this.setHighlightProps(
                    this.props.jobsSearchList.activeItems.items,
                    jobId,
                );
                this.props.jobsSearchList.inactiveItems.items = this.setHighlightProps(
                    this.props.jobsSearchList.inactiveItems.items,
                    jobId,
                );
            } else {
                this.jobsSearchList.highlightJobItem(jobId, onReady);
            }
        }

        const tempOldProps = { ...this.props };
        this.props = { ...this.props, ...response };

        response.jobDetailContent.jobDetail.noCache = noCache;
        response.jobDetailContent.jobHeader.hideWatchlistLoginInfo = true;

        this.jobDetail.updateProps({
            ...response.jobDetailContent,
        }, true);

        if (!mediaQueries.isMobile() && this.jobsSearchList.jobsInfoBox
            && this.jobsSearchList.jobsInfoBox.props.active) {
            this.jobsSearchList.updateInfoBox({ active: false });
        }

        if (mediaQueries.isMobile()) {
            this.jobDetail.openModal();
        }

        selectedJobId = response.jobDetailContent.jobDetail.id;
        const tempSearchFormProps = { ...this.props.jobsSearchform };

        if (mediaQueries.isMobile() && onReady) {
            /* Two History entries are needed if the user bookmarks an opened jobDetail on his mobile phone.
            We write a second history entry, which allows us to close the jobDetail on history back and show the correct
            listing to the user.
            */

            if (jobDetailContentActive) {
                this.replaceState(
                    this.getProps(),
                    document.title,
                    this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                );
            } else {
                this.replaceState(
                    tempOldProps,
                    document.title,
                    this.generateCleanUrl(this.props.jobsSearchform, false),
                );
                this.pushState(
                    this.getProps(),
                    document.title,
                    this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                );
            }
        } else if (replaceState) {
            this.replaceState(
                this.getProps(),
                response.layout.meta.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
            );
        } else {
            this.pushState(
                this.getProps(),
                response.layout.meta.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                false,
            );
        }

        if (!onReady) {
            const url = `${ENDPOINT_JOBS}/${jobId}`;
            this.sendAdWordsTracking(jobId);
            setPageType(response.layout.jobBundleLabel);
            sendPageView(title, url, response.jobDetailContent.jobDetail);
            if (this.jobDetail) this.jobDetail.sendShowEvents();
        }
    }

    setHighlightProps(items, jobId) {
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].jobsItem
                && items[i].jobsItem.id === jobId) {
                items[i].jobsItem.isHighlighted = true;
            } else if (items[i].jobsItem) {
                items[i].jobsItem.isHighlighted = false;
            }
        }
        return items;
    }

    onJobDetailError(error) {
        if (error.status === 0) {
            // aborted xhr ...
            return;
        }

        if (error.status === 404) {
            const data = error.errors[0];
            const viewData = data.meta ? data.meta.viewData : null;
            const tempSearchFormProps = { ...this.props.jobsSearchform };
            const tempProps = { ...this.getProps(), ...viewData };

            if (viewData && viewData.jobsInfoBox) {
                this.jobsSearchList.updateInfoBox(Object.assign(viewData.jobsInfoBox, { wrap: true }));
                this.replaceState(
                    tempProps,
                    document.title,
                    this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                );
            } else {
                selectedJobId = false;
                this.replaceState(tempProps, document.title, this.generateCleanUrl(tempSearchFormProps));
            }
        }

        this.request = false;
        this.hideOverlay('detail');
    }

    newSearchRequest(diffRequest, sendGa4Tracking = true) {
        this.showOverlay('listing');
        if (selectedJobId && !mediaQueries.isMobile()) {
            this.showOverlay('detail');
        }
        selectedJobId = false;

        this.sendSearchRequest(
            {
                ...this.props.jobsSearchform,
                ...diffRequest,
            },
            false,
            false,
            true,
            sendGa4Tracking,
        );
    }

    changedSearchRequest(diffRequest) {
        this.showOverlay('listing');
        if (selectedJobId && !mediaQueries.isMobile()) {
            this.showOverlay('detail');
        }
        selectedJobId = false;

        this.sendSearchRequest({ ...this.props.jobsSearchform, ...diffRequest });
    }

    sendSearchRequest(
        searchRequest,
        replaceState = false,
        jobDetailContentActive = false,
        sendShowEvents = true,
        sendGa4Tracking = true,
    ) {
        const endpoint = this.generateCleanUrl(searchRequest, true, true);
        if (this.searchRequest) {
            this.searchRequest.abort();
        }
        this.searchRequest = getJson(
            endpoint,
            {},
            (response) => this.onSearchResponse(
                response,
                replaceState,
                jobDetailContentActive,
                sendShowEvents,
                sendGa4Tracking,
            ),
            (error) => this.onSearchResponseError(error, replaceState),
        );
    }

    onSearchResponse(
        responseJson,
        replaceState,
        jobDetailContentActive = false,
        sendShowEvents = true,
        sendGa4Tracking = true,
    ) {
        // remove watchlist listeners for jobs everytime we render new jobs ...
        userWatchlist.events.removeAllListeners();
        this.addLoginModalListener();

        const jobsInfoBoxActive = this.props.jobsInfoBox !== undefined;

        if (jobsInfoBoxActive) {
            if (responseJson.jobsSearchList !== undefined) {
                responseJson.jobsSearchList.jobsInfoBox = this.props.jobsInfoBox;
            }
            delete this.props.jobsInfoBox;
        }

        if (jobDetailContentActive) {
            delete responseJson.layout;
            delete responseJson.jobDetailContent;
            if (!mediaQueries.isMobile()) {
                if (responseJson.jobsEmptyState) responseJson.jobsEmptyState.active = false;
                const jobId = this.props.jobDetailContent?.jobDetail?.id;
                if (responseJson.jobsSearchList !== undefined) {
                    for (let i = 0; i < responseJson.jobsSearchList.activeItems.items.length; i += 1) {
                        const { jobsItem } = responseJson.jobsSearchList.activeItems.items[i];
                        if (jobsItem && jobsItem.id === jobId) {
                            responseJson.jobsSearchList.activeItems.items[i].jobsItem.isHighlighted = true;
                        } else if (jobsItem) {
                            responseJson.jobsSearchList.activeItems.items[i].jobsItem.isHighlighted = false;
                        }
                    }
                    for (let i = 0; i < responseJson.jobsSearchList.inactiveItems.items.length; i += 1) {
                        const { jobsItem } = responseJson.jobsSearchList.inactiveItems.items[i];
                        if (jobsItem && jobsItem.id === jobId) {
                            responseJson.jobsSearchList.inactiveItems.items[i].jobsItem.isHighlighted = true;
                        } else if (jobsItem) {
                            responseJson.jobsSearchList.inactiveItems.items[i].jobsItem.isHighlighted = false;
                        }
                    }
                }
            } else {
                delete responseJson.jobsEmptyState;
            }
        }

        // persist last selected radius index
        const { selectedRadiusIndex } = this.props;
        responseJson.jobsSearchform.proximity.selectedIndex = selectedRadiusIndex;

        // persist job search form open state
        responseJson.jobsSearchform.collapsedSearchform = this.jobsSearchform.props.collapsedSearchform;

        this.updateProps(responseJson);

        // html5 history
        const tempSearchFormProps = { ...this.props.jobsSearchform };
        if (replaceState) {
            this.replaceState(
                this.getProps(),
                this.props.layout?.meta?.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
            );
        } else {
            this.pushState(
                this.getProps(),
                this.props.layout?.meta?.title,
                this.generateCleanUrl(Object.assign(tempSearchFormProps, { selectedJobId })),
                false,
            );
        }

        this.hideOverlay('listing');
        this.hideOverlay('detail');

        if (!localStorage?.getItem?.('wasOpen')
            && !this.alarmDisruptor.props.isLoggedIn) {
            clearTimeout(timeout);
            timeout = setTimeout(() => this.alarmDisruptor.openAlarmBox(true), 10000);
        }

        sendPageView();

        if (sendShowEvents) {
            this.sendShowEvents();
            this.sendListTracking();
        }

        this.addToJobSearchCache();

        // a11y - Shift focus to the search results
        document.getElementById('jobsListHeaderScreenReaderHelper').focus();

        if (sendGa4Tracking) {
            this.sendGa4Tracking(responseJson);
        }
    }

    onSearchResponseError(error, replaceState) {
        if (error.status === 0) {
            // aborted xhr ...
            return;
        }

        this.updateProps({ error });

        const searchRequest = { page: 1,
            ...this.jobsSearchform.getPropsFromDom() };

        if (replaceState) {
            this.replaceState(this.getProps(), `error ${error.status}`, this.generateCleanUrl(searchRequest));
        } else {
            this.pushState(this.getProps(), `error ${error.status}`, this.generateCleanUrl(searchRequest));
        }

        this.hideOverlay('detail');
        this.hideOverlay('listing');
    }

    loadMoreJobsRequest(diffRequest) {
        this.sendMoreJobsRequest({ ...this.props.jobsSearchform, ...diffRequest });
    }

    sendMoreJobsRequest(searchRequest) {
        const endpoint = this.generateCleanUrl(searchRequest, true, true);
        this.searchRequest?.abort();
        this.searchRequest = getJson(
            endpoint,
            {},
            (response) => this.onMoreJobsResponse(response),
            (error) => this.onMoreJobsError(error),
        );
    }

    onMoreJobsResponse(responseJson) {
        const { items: itemsNew } = responseJson.jobsSearchList.activeItems;
        const jobsListItemToItem = (jobsListItem) => ({ jobsItem: jobsListItem.props });
        const itemsOld = [...this.jobsSearchList.jobsActiveList.jobsListItems].map(jobsListItemToItem);

        const getJobItemId = (item) => item.jobsItem?.id;
        const idsOldSet = new Set(itemsOld.map(getJobItemId));

        const hasKnownId = (item) => idsOldSet.has(getJobItemId(item));
        const itemDuplicates = itemsNew.filter(hasKnownId);

        const isNonDuplicate = (item) => !itemDuplicates.map(getJobItemId).includes(getJobItemId(item));
        const itemsUnique = itemsNew.filter(isNonDuplicate);
        this.jobsSearchList.jobsActiveList.appendItemsToList(itemsUnique);
        this.jobsSearchList.loadMoreJobsButton.updateProps(responseJson.jobsSearchList.loadMoreJobsButton, true);

        readScreenReaderText('Jobs wurden geladen. Tab führt zum ersten Ergebnis.');
        const url = this.generateCleanUrl(
            {
                ...this.jobsSearchform.getSerializedProps(),
                ...this.jobsSearchList.loadMoreJobsButton.getJobsSearchProps(),
            },
        );
        sendPageView(document.title, url);
        this.sendListTrackingForLateLoadedJobs(itemsUnique);
    }

    onMoreJobsError(error) {
        if (error.status === 0) {
            // aborted xhr ...
            return;
        }
        this.updateProps({ error });
    }

    generateCleanUrl(searchRequest, withSelectedJobId = true, generateEndpoint = false) {
        const urlParts = [];
        const urlParams = {};
        const encodedUrlParams = {};
        urlParts.push(ENDPOINT_JOBS);
        if (generateEndpoint) {
            if (searchRequest.keywords) {
                urlParams.keywords = searchRequest.keywords;
            } else if (searchRequest.keywordSlug) {
                encodedUrlParams.keywords = searchRequest.keywordSlug;
            }
            if (searchRequest.locations) {
                urlParams.locations = searchRequest.locations;
            } else if (searchRequest.locationSlug) {
                encodedUrlParams.locations = searchRequest.locationSlug;
            }
        } else {
            if (searchRequest.keywordSlug) {
                urlParts.push(searchRequest.keywordSlug);
            } else if (searchRequest.keywords) {
                urlParts.push(stringToSlug(searchRequest.keywords));
            }
            if (searchRequest.locationSlug) {
                urlParts.push(searchRequest.locationSlug);
            } else if (searchRequest.locations) {
                urlParts.push(stringToSlug(searchRequest.locations));
            }
        }

        const url = urlParts.join('/');

        const radius = parseInt(searchRequest.radius, 10);
        // Default radius should not be part of the url
        if (radius >= 0 && radius !== 20) {
            urlParams.radius = radius;
        }
        if (searchRequest.page > 1) {
            urlParams.page = searchRequest.page;
        }
        if (searchRequest['jobFields[]']) {
            urlParams.jobFields = searchRequest['jobFields[]'];
        }
        if (searchRequest['employmentTypes[]']) {
            urlParams.employmentTypes = searchRequest['employmentTypes[]'];
        }
        if (searchRequest.homeoffice && searchRequest.homeoffice !== 0) {
            urlParams.homeoffice = searchRequest.homeoffice;
        }
        if (searchRequest['jobLevels[]']) {
            urlParams.jobLevels = searchRequest['jobLevels[]'];
        }
        if (searchRequest['salary[]'] && searchRequest['salary[]'][0] !== defaultSalaryId) {
            urlParams.salary = searchRequest['salary[]'];
        }
        if (searchRequest.company) {
            urlParams.company = searchRequest.company;
        }
        if (withSelectedJobId && searchRequest.selectedJobId) {
            return `${urlWithParams(url, urlParams, encodedUrlParams)}#${searchRequest.selectedJobId}`;
        }

        return urlWithParams(url, urlParams, encodedUrlParams);
    }

    hideOverlay(type) {
        if (type === 'listing') {
            this.loadingOverlayListing.hide();
            if (mediaQueries.isMobile()) {
                document.body.style.overflow = 'auto';
            }
        }
        if (type === 'detail') this.loadingOverlayDetail.hide();
    }

    showOverlay(type) {
        if (type === 'listing' && !this.loadingOverlayListing.props.visible) {
            window.scrollTo(0, 0);
            this.loadingOverlayListing.show();
            if (mediaQueries.isMobile()) {
                document.body.style.overflow = 'hidden';
            }
        }
        if (type === 'detail' && !this.loadingOverlayDetail.props.visible) this.loadingOverlayDetail.show();
    }

    filterRequest(request) {
        Object.keys(request).forEach((key) => {
            if ((key === 'radius' && request[key] === '0')
                || (key === 'page' && request[key] === 1)) {
                delete request[key];
            }
        });

        return request;
    }

    addModalStyles() {
        if (!mediaQueries.isMobile()) {
            this.dom.bgContainer.classList.add('c-jobsSearch__bgContainer--modalOpen');
            this.dom.alarmDisruptorPillContainer.classList.add('c-jobsSearch__alarmDisruptor--modalOpen');
        }
    }

    removeModalStyles() {
        if (!mediaQueries.isMobile()) {
            this.dom.bgContainer.classList.remove('c-jobsSearch__bgContainer--modalOpen');
            this.dom.alarmDisruptorPillContainer.classList.remove('c-jobsSearch__alarmDisruptor--modalOpen');
        }
    }

    sendShowEvents() {
        if (this.jobsSearchList.jobsInactiveList
            && this.jobsSearchList.jobsInactiveList.jobsListItems.length > 0) {
            trackGa4({
                event: 'show_element',
                element: 'JOBS: Inactive Jobs',
                element_detail: null,
            });
        }
    }

    sendListTracking() {
        const items = [];
        const jobItems = this.jobsSearchList.jobsActiveList.jobsListItems;

        for (let i = 0; i < jobItems.length; i += 1) {
            const flags = [];

            if (jobItems[i].props.company.hasBranding) {
                flags.push('Einblicke');
            }
            if (jobItems[i].props.company.isBrandingSolution) {
                flags.push('Videos');
            }

            items.push({
                type: 'job',
                variant: jobItems[i].props.pushedJob ? 'Topjob' : 'Job',
                list: 'jobsearch',
                id: jobItems[i].props.id,
                name: jobItems[i].props.title,
                company: jobItems[i].props.company.name,
                flags,
                position: i + 1,
            });
        }

        window.dataLayer.push({
            event: 'impressions',
            items,
            _clear: true,
        });
    }

    sendListTrackingForLateLoadedJobs(items) {
        const trackingItems = [];
        let i = 0;
        items.forEach((item) => {
            Object.keys(item).forEach((key) => {
                if (key === 'jobsItem') {
                    const flags = [];
                    if (item[key].company.hasBranding) {
                        flags.push('Einblicke');
                    }
                    if (item[key].company.isBrandingSolution) {
                        flags.push('Videos');
                    }

                    trackingItems.push({
                        type: 'job',
                        variant: item[key].pushedJob ? 'Topjob' : 'Job',
                        list: 'jobsearch',
                        id: item[key].id,
                        name: item[key].title,
                        company: item[key].company.name,
                        flags,
                        position: i + 1,
                    });

                    i += 1;
                }
            });
        });

        window.dataLayer.push({
            event: 'impressions',
            items: trackingItems,
            _clear: true,
        });
    }

    addLoginModalListener() {
        if (!user.isLoggedIn()) {
            userWatchlist.events.on('onUnauthorized', (watchlistObject) => {
                this.layout.openLoginModal(watchlistObject.arguments[0].watchlistObject.state, true, true);
            });
        }
    }

    onFirstUserInteraction() {
        if (this.jobsSearchList.hokifyJobsAd) {
            this.jobsSearchList.hokifyJobsAd.load();
        }
        if (mediaQueries.isMobile()) {
            this.dom.alarmDisruptorPillContainer.classList.add('c-jobsSearch__alarmDisruptor--slide');
        }
    }

    sendAdWordsTracking(jobId) {
        window.dataLayer.push({
            googleAdsData: {
                job_pagetype: 'offerdetail',
                job_id: jobId,
            },
        });
    }

    sendGa4Tracking(response = null) {
        const filtersArray = [];
        let filters = '';
        let activeJobFieldFilterIds = null;
        let activeEmploymentTypeFilterIds = null;
        let activeJobLevelFilterIds = null;

        if (response !== null) {
            for (let i = 0; i < response.filter.items.length; i += 1) {
                if (response.filter.items[i].count > 0) {
                    if (response.filter.items[i].category) {
                        filtersArray.push(response.filter.items[i].category);
                    } else if (response.filter.items[i].name) {
                        filtersArray.push(response.filter.items[i].name);
                    }
                }
            }

            if (this.props.jobsSearchform.radius) {
                filtersArray.push('radius');
            }

            filters = filtersArray.join(', ');

            // Get ids of selected employment types, job fields and job level filters
            const { jobFields, jobLevels, employmentTypes } = this.extractCheckedFilterIds(response.filter);
            activeJobFieldFilterIds = jobFields;
            activeEmploymentTypeFilterIds = employmentTypes;
            activeJobLevelFilterIds = jobLevels;
        }

        let keywords = null;

        if (this.props.jobsSearchform.keywords) {
            if (this.props.jobsSearchform.keywords.length) {
                keywords = this.props.jobsSearchform.keywords.toLowerCase();
            }
        }

        let locations = null;

        if (this.props.jobsSearchform.locations) {
            if (this.props.jobsSearchform.locations.length) {
                locations = this.props.jobsSearchform.locations.toLowerCase();
            }
        }

        if (keywords || locations || filters.length > 0) {
            trackGa4({
                event: 'search',
                keywords,
                locations,
                filters: filters.length ? filters : null,
                job_fields: activeJobFieldFilterIds,
                employment_types: activeEmploymentTypeFilterIds,
                job_levels: activeJobLevelFilterIds,
                form_type: 'Job Search',
            });
        }
    }

    extractCheckedFilterIds(filter) {
        const result = {
            employmentTypes: [],
            jobFields: [],
            jobLevels: [],
        };

        // Iterate through each item in the filter
        filter.items.forEach((category) => {
            // Check if the category is one we care about
            if (category.category in result) {
                // Iterate through each item in this category
                category.items.forEach((item) => {
                    // If the item is checked, add its value to the result array
                    if (item.checked) {
                        result[category.category].push(item.value);
                    }
                });
            }
        });

        Object.keys(result).forEach((key) => {
            result[key] = result[key].length ? result[key].join(', ') : null;
        });

        return result;
    }
}

app.boot(JobsSearchController);

if (module.hot) {
    module.hot.accept(module.children, () => {
        // eslint-disable-next-line no-console
        console.log('Rebooting controller due to HMR...');
        app.boot(JobsSearchController);
    });

    module.hot.accept(() => {});
}
